import { useState, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useContext } from "react";
import TimePicker from "components/timepicker";
import dateTime from "utils/helpers/dateTime";
import { MobXProviderContext } from "mobx-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import object from "utils/helpers/object";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import {
  useMealNextAvailableDate,
  getValidMealList,
} from "utils/hooks/useMealNextAvailableDate";
import { Calendar } from "react-multi-date-picker";
import classNames from "classnames";

function ChangeDeliveryTimeMealPlan({
  visible,
  setModal,
  time,
  updatingMeal = false,
  mealId,
  tzinfo,
  meals,
}) {
  const [deliveryTime, setDeliveryTime] = useState("");
  const [warning, setWarning] = useState(false);

  const nextAvailableMeal = useMealNextAvailableDate(meals);
  const validMeals = getValidMealList(meals);

  const [changeEffect, setChangeEffect] = useState("next");
  const [selectedCustomDate, setSelectedCustomDate] = useState(null);

  const { mealManagementStore } = useContext(MobXProviderContext);
  const { userStore } = useContext(MobXProviderContext);
  const calendarRef = useRef(null);

  const mealPlan = mealManagementStore.mealPlan;
  const { locationID } = userStore;

  const handleTimeInput = (time) => {
    setDeliveryTime(dateTime.to24hourformat(time));
  };

  const onSavePress = () => {
    if (!warning && object.isPresent(nextAvailableMeal)) {
      setWarning(true);
    } else {
      let payload = {
        meal_series: {
          target_time: deliveryTime,
        },
      };

      if (object.isPresent(nextAvailableMeal)) {
        payload.meal_series.effective_date =
          changeEffect == "custom"
            ? selectedCustomDate ?? nextAvailableMeal?.mealDate
            : nextAvailableMeal?.mealDate;
      }

      mealManagementStore
        .sendDeliveryTimeRequest(locationID, mealPlan?.id, payload)
        .then(() => setModal(false));
    }
  };

  const handleDateChange = (value) => {
    const selectedDate = dayjs(value.toDate()).format("YYYY-MM-DD");

    setSelectedCustomDate(selectedDate);
  };

  return (
    <Transition appear show={visible} as={Fragment} avoidClose={true}>
      <Dialog as="div" className="relative z-[90]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center sm:p-4 md:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              {warning ? (
                <Dialog.Panel className="w-[696px] min-h-[276px] d-col justify-between rounded-xl md:w-6/12 transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all overflow-visible">
                  <div className="d-col">
                    <div className="flex justify-between items-center mb-[22px]">
                      <button
                        className="font-inter-regular text-[16px] text-hh-accent-dark"
                        onClick={() => {
                          setWarning(false);
                          setSelectedCustomDate(null);
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          size="xs"
                          className="mr-2"
                          style={{
                            color: "#130f26",
                          }}
                        />
                        Back
                      </button>

                      <button
                        type="button"
                        onClick={() => setModal(false)}
                        className="btn-style"
                      >
                        <FontAwesomeIcon
                          style={{ width: 16, height: 26 }}
                          icon={faXmark}
                          size="2x"
                          color="#BBC0C5"
                        />
                      </button>
                    </div>

                    <h2 className="text-2xl font-inter-bold text-dark-gray">
                      Set Date for Changes
                    </h2>

                    <small className="font-inter-regular text-dark-gray text-[16px] mt-[16px] bg-light-gray py-[12px] px-[8px] rounded-lg">
                      Delivery time change requested to {deliveryTime} These
                      changes will apply to all future meals in the series.
                    </small>

                    <strong className="text-dark-gray font-inter-semibold text-[16px] my-[16px]">
                      When should they take effect?
                    </strong>

                    <div className="d-col rounded-lg">
                      <div key="next" className="flex items-center">
                        <input
                          name="date-method"
                          type="radio"
                          value="next"
                          checked={changeEffect == "next"}
                          onChange={(e) => setChangeEffect(e.target.value)}
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="photo"
                          className="ml-3 block text-sm font-inter-regular leading-6 text-gray-900"
                        >
                          Next available meal in series:{" "}
                          {nextAvailableMeal?.mealDate}
                        </label>
                      </div>

                      <div key="custom" className="flex items-center mt-[16px]">
                        <input
                          name="date-method"
                          type="radio"
                          value={"custom"}
                          checked={changeEffect == "custom"}
                          onChange={(e) => setChangeEffect(e.target.value)}
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="signature"
                          className="ml-3 block text-sm font-inter-regular leading-6 text-gray-900"
                        >
                          Pick a custom date
                        </label>
                      </div>
                    </div>

                    {changeEffect == "custom" && (
                      <div
                        className="flex flex-row justify-center items-center mt-[16px]"
                        ref={calendarRef}
                      >
                        <Calendar
                          highlightToday={true}
                          className="shadow-lg"
                          isMulti={false}
                          onChange={handleDateChange}
                          disableYearPicker={true}
                          monthYearSeparator={" "}
                          mapDays={({ date }) => {
                            const allowedDates = validMeals?.map(
                              (meal) => meal?.meal_date
                            );
                            const formatted = dayjs(date.toDate()).format(
                              "YYYY-MM-DD"
                            );
                            const isEnabled = allowedDates.includes(formatted);

                            return {
                              disabled: !isEnabled,
                              style: isEnabled
                                ? { color: "#000" }
                                : {
                                    color: "#ccc",
                                    textDecoration: "",
                                    pointerEvents: "none",
                                  },
                            };
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-center items-center mt-5">
                    <button
                      className="btn-outline btn-style w-full"
                      onClick={() => setModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className={classNames({
                        "btn-purple btn-style px-6 ml-3 w-full": true,
                        "btn-disabled":
                          changeEffect === "custom" && !selectedCustomDate,
                      })}
                      onClick={onSavePress}
                      disabled={changeEffect == "custom" && !selectedCustomDate}
                    >
                      Request changes
                    </button>
                  </div>
                </Dialog.Panel>
              ) : (
                <Dialog.Panel className="w-[696px] min-h-[276px] d-col justify-between rounded-xl md:w-6/12 transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all overflow-visible">
                  <div className="d-row justify-between items-center">
                    <h2 className="text-2xl font-inter-bold text-dark-gray">
                      Change Meal Plan Delivery Time
                    </h2>

                    <button
                      onClick={() => setModal(false)}
                      className="text-dark-gray hover:text-yellow-600"
                      aria-label="Close"
                    >
                      <span className="text-xl">&times;</span>
                    </button>
                  </div>

                  <span className="font-inter-regular my-[16px] text-secondary text-[16px]">
                    These changes will apply to all future meals in the series
                    and not for a single specific day.
                  </span>

                  <span className="font-inter-regular">
                    Select requested delivery time
                    {dateTime.checkAndGetTimezone(tzinfo) &&
                      ` (${dateTime.checkAndGetTimezone(tzinfo)})`}
                  </span>

                  <div className="w-[142px] mt-[16px]">
                    <TimePicker
                      ideal_time={dateTime.formatTimeWithFormat(
                        time,
                        "hh:mm A",
                        tzinfo
                      )}
                      datetime={handleTimeInput}
                      meal_name="other"
                    />
                  </div>

                  <div className="d-row w-full items-center mt-[24px] justify-center">
                    <button
                      className="btn-light btn-style py-3 px-5 w-full"
                      onClick={() => setModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn-purple btn-style py-3 px-6 ml-3 w-full"
                      onClick={onSavePress}
                    >
                      Next
                    </button>
                  </div>
                </Dialog.Panel>
              )}
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default ChangeDeliveryTimeMealPlan;
